/* =========================================================
   VISTOS RECIENTEMENTE - CABELLOS CELINA
   ========================================================= */

(() => {
  'use strict';

  const STORAGE_KEY = 'cc_recently_viewed_handles_v1';
  const PRODUCT_CACHE_PREFIX = 'cc_product_cache_v1:';
  const CACHE_TTL = 6 * 60 * 60 * 1000;

  const readJSON = (key, fallback) => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  };

  const writeJSON = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {}
  };

  const escapeHTML = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const money = (cents) => {
    try {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0
      }).format(Number(cents || 0) / 100);
    } catch (e) {
      return `$ ${Math.round(Number(cents || 0) / 100).toLocaleString('es-CO')}`;
    }
  };

  class RecentCelina {
    constructor(root) {
      this.root = root;
      this.currentHandle = root.dataset.currentHandle || '';
      this.maxItems = Number(root.dataset.limit || 30);
      this.cardWidth = Number(root.dataset.cardWidth || 230);
      this.scrollStep = Number(root.dataset.scrollStep || 3);
      this.showVendor = root.dataset.showVendor === 'true';
      this.showPrice = root.dataset.showPrice === 'true';
      this.showRemove = root.dataset.showRemove === 'true';

      this.track = root.querySelector('[data-cc-track]');
      this.viewport = root.querySelector('[data-cc-viewport]');
      this.prev = root.querySelector('[data-cc-prev]');
      this.next = root.querySelector('[data-cc-next]');
      this.clear = root.querySelector('[data-cc-clear]');
      this.progress = root.querySelector('[data-cc-progress]');

      this.observer = null;
      this.handles = [];
      this.products = [];

      this.init();
    }

    async init() {
      this.recordCurrentProduct();
      this.handles = this.getHandles();

      if (!this.handles.length) {
        this.root.hidden = true;
        return;
      }

      this.products = await this.loadProducts(this.handles);

      if (!this.products.length) {
        this.root.hidden = true;
        return;
      }

      this.render();
      this.bind();
      this.root.hidden = false;

      requestAnimationFrame(() => {
        this.root.classList.add('is-ready');
        this.animateCards();
        this.updateNav();
        this.updateProgress();
      });
    }

    getHandles() {
      const list = readJSON(STORAGE_KEY, []);
      return Array.isArray(list) ? list.filter(Boolean).slice(0, this.maxItems) : [];
    }

    setHandles(list) {
      const unique = [...new Set(list.filter(Boolean))].slice(0, this.maxItems);
      writeJSON(STORAGE_KEY, unique);
      this.handles = unique;
    }

    recordCurrentProduct() {
      if (!this.currentHandle) return;

      const current = this.getHandles().filter((h) => h !== this.currentHandle);
      current.unshift(this.currentHandle);
      this.setHandles(current);
    }

    async loadProducts(handles) {
      const results = await Promise.all(
        handles.map(async (handle) => {
          try {
            return await this.fetchProduct(handle);
          } catch (e) {
            return null;
          }
        })
      );
      return results.filter(Boolean);
    }

    async fetchProduct(handle) {
      const cacheKey = `${PRODUCT_CACHE_PREFIX}${handle}`;
      const cached = readJSON(cacheKey, null);

      if (cached?.data && cached?.time && Date.now() - cached.time < CACHE_TTL) {
        return cached.data;
      }

      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) throw new Error(`Product fetch failed: ${handle}`);

      const data = await response.json();
      const normalized = {
        handle,
        title: data.title || '',
        vendor: data.vendor || '',
        url: data.url || `/products/${handle}`,
        price: data.price || 0,
        image: data.featured_image || (Array.isArray(data.images) && data.images.length ? data.images[0] : '')
      };

      writeJSON(cacheKey, { time: Date.now(), data: normalized });
      return normalized;
    }

    render() {
      if (!this.track) return;
      this.track.innerHTML = this.products.map((p, i) => this.cardTemplate(p, i)).join('');
    }

    cardTemplate(product, index) {
      const image = product.image
        ? `<img class="cc-recent__image" src="${escapeHTML(product.image)}" alt="${escapeHTML(product.title)}" loading="lazy">`
        : `<div class="cc-recent__placeholder" aria-hidden="true"></div>`;

      return `
        <article class="cc-recent__card" data-cc-card data-handle="${escapeHTML(product.handle)}" style="--cc-card-delay:${420 + index * 70}ms;">
          ${this.showRemove ? `<button type="button" class="cc-recent__remove" data-cc-remove="${escapeHTML(product.handle)}" aria-label="Eliminar ${escapeHTML(product.title)}"></button>` : ''}
          <a class="cc-recent__card-link" href="${escapeHTML(product.url)}">
            <div class="cc-recent__image-wrap">${image}</div>
            <div class="cc-recent__body">
              ${this.showVendor && product.vendor ? `<div class="cc-recent__vendor">${escapeHTML(product.vendor)}</div>` : ''}
              <h3 class="cc-recent__name">${escapeHTML(product.title)}</h3>
              ${this.showPrice ? `<div class="cc-recent__price">${money(product.price)}</div>` : ''}
            </div>
          </a>
        </article>
      `;
    }

    bind() {
      this.prev?.addEventListener('click', () => this.scrollByCards(-this.scrollStep));
      this.next?.addEventListener('click', () => this.scrollByCards(this.scrollStep));
      this.clear?.addEventListener('click', () => this.clearAll());

      this.viewport?.addEventListener('scroll', () => {
        this.updateNav();
        this.updateProgress();
      }, { passive: true });

      this.track?.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-cc-remove]');
        if (!remove) return;
        event.preventDefault();
        event.stopPropagation();
        this.removeOne(remove.dataset.ccRemove);
      });

      this.bindPointerTilt();

      window.addEventListener('resize', () => {
        this.updateNav();
        this.updateProgress();
      });
    }

    animateCards() {
      const cards = [...this.root.querySelectorAll('[data-cc-card]')];

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
        cards.forEach((card) => card.classList.add('is-visible'));
        return;
      }

      this.observer = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.12 });

      cards.forEach((card) => this.observer.observe(card));
    }

    bindPointerTilt() {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      this.root.querySelectorAll('[data-cc-card]').forEach((card) => {
        card.addEventListener('pointermove', (event) => {
          const rect = card.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width;
          const y = (event.clientY - rect.top) / rect.height;
          card.style.setProperty('--cc-ry', `${((x - .5) * 3.2).toFixed(2)}deg`);
          card.style.setProperty('--cc-rx', `${((.5 - y) * 3.2).toFixed(2)}deg`);
        });

        card.addEventListener('pointerleave', () => {
          card.style.setProperty('--cc-rx', '0deg');
          card.style.setProperty('--cc-ry', '0deg');
        });
      });
    }

    scrollByCards(count) {
      if (!this.viewport) return;
      const firstCard = this.root.querySelector('[data-cc-card]');
      const gap = parseFloat(getComputedStyle(this.track).gap || 0);
      const width = firstCard?.getBoundingClientRect().width || this.cardWidth;

      this.viewport.scrollBy({
        left: count * (width + gap),
        behavior: 'smooth'
      });
    }

    updateNav() {
      if (!this.viewport) return;
      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      const left = this.viewport.scrollLeft;
      this.prev?.classList.toggle('is-disabled', left <= 2);
      this.next?.classList.toggle('is-disabled', left >= maxScroll - 2);
    }

    updateProgress() {
      if (!this.viewport || !this.progress) return;
      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      const ratio = maxScroll <= 0 ? 1 : Math.min(1, Math.max(.08, this.viewport.scrollLeft / maxScroll));
      this.progress.style.transform = `scaleX(${ratio})`;
    }

    removeOne(handle) {
      const next = this.getHandles().filter((item) => item !== handle);
      this.setHandles(next);

      const card = this.root.querySelector(`[data-handle="${CSS.escape(handle)}"]`);
      if (!card) return;

      card.style.opacity = '0';
      card.style.transform = 'translateY(10px) scale(.97)';

      setTimeout(() => {
        card.remove();
        if (!this.root.querySelector('[data-cc-card]')) {
          this.root.hidden = true;
        } else {
          this.updateNav();
          this.updateProgress();
        }
      }, 280);
    }

    clearAll() {
      this.setHandles([]);

      try {
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith(PRODUCT_CACHE_PREFIX)) localStorage.removeItem(key);
        });
      } catch (e) {}

      this.root.style.opacity = '0';
      this.root.style.transform = 'translateY(10px)';

      setTimeout(() => {
        this.root.hidden = true;
      }, 300);
    }

    destroy() {
      this.observer?.disconnect();
    }
  }

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-cc-recent]').forEach((root) => {
      if (root.__ccRecent) return;
      root.__ccRecent = new RecentCelina(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-cc-recent]').forEach((root) => {
      root.__ccRecent?.destroy();
      root.__ccRecent = null;
    });
  });
})();
